import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artstorefronts/ui';
import type { AdminBounty } from './bounty-shared';

/**
 * The settings of a bounty its engine pays as it happens (#4118): first-touch, and the like
 * and comment payouts. It has no period. How often is Every time (no limit) or Every day
 * (every time, up to a limit per member per day) — the limit box shows only for Every day.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
export const FIRST_TOUCH_KEY = 'first-touch';

type Field = { key: string; label: string; hint: string; scale?: number; blankIsNoLimit?: boolean };

/** First-touch's numbers. The window is stored in ms and typed in days. */
const FIRST_TOUCH_FIELDS: Field[] = [
  { key: 'points', label: 'Points per artist', hint: 'At most 5.' },
  {
    key: 'recentSignupMs',
    label: 'New-artist window (days)',
    hint: 'Pays for artists who signed up within this many days. At most 30.',
    scale: DAY_MS,
  },
  {
    key: 'maxEarnersPerArtist',
    label: 'People paid per artist',
    hint: 'Leave blank for no limit. At most 10.',
    blankIsNoLimit: true,
  },
];

const PAYOUT_FIELDS: Field[] = [
  { key: 'points', label: 'Points each time', hint: 'Up to five times what it paid at launch.' },
];

/** The key the daily limit is stored under, and its ceiling, for this engine. */
export function dailyLimitField(systemKey: string): Field {
  if (systemKey === FIRST_TOUCH_KEY) {
    return { key: 'dailyCapUtc', label: 'Times per member per UTC day', hint: 'At most 25.' };
  }
  return { key: 'dailyCap', label: 'Times per member per UTC day', hint: 'At most 1,000.' };
}

function fieldsFor(systemKey: string): Field[] {
  return systemKey === FIRST_TOUCH_KEY ? FIRST_TOUCH_FIELDS : PAYOUT_FIELDS;
}

function scaleOf(systemKey: string, key: string): number {
  return fieldsFor(systemKey).find((field) => field.key === key)?.scale ?? 1;
}

/** Stored config → what the admin sees: no limit is a blank box, the window is days. */
export function engineConfigToForm(
  systemKey: string,
  config: Record<string, number | null> | null | undefined
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(config ?? {}).map(([k, v]) => [k, v === null ? '' : String(v / scaleOf(systemKey, k))])
  );
}

/** What the admin typed → stored config. Every time stores no daily limit. */
export function engineConfigToInput(
  systemKey: string,
  cadence: AdminBounty['cadence'],
  config: Record<string, string>
): Record<string, number | null> {
  const limitKey = dailyLimitField(systemKey).key;
  const out: Record<string, number | null> = {};
  for (const field of fieldsFor(systemKey)) {
    const typed = (config[field.key] ?? '').trim();
    out[field.key] = field.blankIsNoLimit && typed === '' ? null : Math.round(Number(typed) * (field.scale ?? 1));
  }
  out[limitKey] = cadence === 'daily' ? Number(config[limitKey] ?? '') : null;
  return out;
}

export function EngineBountySettings({
  systemKey,
  cadence,
  enrollment,
  config,
  onCadence,
  onEnrollment,
  onConfig,
}: {
  systemKey: string;
  cadence: AdminBounty['cadence'];
  enrollment: AdminBounty['enrollment'];
  config: Record<string, string>;
  onCadence: (value: AdminBounty['cadence']) => void;
  onEnrollment: (value: AdminBounty['enrollment']) => void;
  onConfig: (value: Record<string, string>) => void;
}) {
  const firstTouch = systemKey === FIRST_TOUCH_KEY;
  const limit = dailyLimitField(systemKey);
  const fields = cadence === 'daily' ? [...fieldsFor(systemKey), limit] : fieldsFor(systemKey);

  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="bounty-engine-settings">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>How often</Label>
          <Select value={cadence} onValueChange={(next) => onCadence(next as AdminBounty['cadence'])}>
            <SelectTrigger data-testid="bounty-engine-cadence">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="each">Every time — no limit</SelectItem>
              <SelectItem value="daily">Every day — up to a daily limit</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-amber-800">
            {cadence === 'daily'
              ? 'Pays each time, up to the limit below per member; the count resets at midnight UTC.'
              : 'Pays each time it happens, with no daily limit.'}
          </p>
        </div>
        {firstTouch ? (
          <div className="space-y-1">
            <Label>Who it is for</Label>
            <Select value={enrollment} onValueChange={(next) => onEnrollment(next as AdminBounty['enrollment'])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Everyone in its audience</SelectItem>
                <SelectItem value="optin">Members who join it</SelectItem>
                <SelectItem value="invite">Invited members only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-amber-800">
              Applies on save. Opt-in or invite-only pays only the members added on the right.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <Label>Who it is for</Label>
            <p className="text-sm text-amber-900">Whoever was liked or replied to.</p>
          </div>
        )}
      </div>
      <p className="text-xs text-amber-800">
        Delivery: secret. It pays as it happens, so nothing shows in the band, inbox or email.
      </p>
      {firstTouch ? (
        <p className="text-sm text-amber-900">
          This bounty once shipped at 500 points and minted 3.09M in 46 hours. Every number here is held to a server
          ceiling, and pays from the moment you save.
        </p>
      ) : (
        <p className="text-sm text-amber-900">
          Pays from the moment you save, and is held to a server ceiling. An undo claws back what was actually paid.
        </p>
      )}
      {fields.map((field) => (
        <div key={field.key} className="space-y-1">
          <Label htmlFor={`b-config-${field.key}`}>{field.label}</Label>
          <Input
            id={`b-config-${field.key}`}
            value={config[field.key] ?? ''}
            onChange={(e) => onConfig({ ...config, [field.key]: e.target.value })}
          />
          <p className="text-xs text-amber-800">{field.hint}</p>
        </div>
      ))}
    </div>
  );
}
